import React, { FC, useMemo, useState } from 'react'

import { Txt } from 'src/components/atoms'
import { Search } from 'src/components/molecules'
import {
  InternalUserList,
  ExternalUserList,
  CreateUserModal,
  MemberTypeSwitchTabs,
  MemberType,
} from 'src/components/organisms'

import { useGetExternalUsers, useGetProfile, useGetUsers } from 'src/hooks'
import { UserEntity } from 'src/types'

type Props = {
  selectedMemberTabType?: MemberType
  className?: string
}

export const UserListPageUserList: FC<Props> = ({
  selectedMemberTabType = 'internal',
  className,
}) => {
  const [searchWord, setSearchWord] = useState<string | undefined>(undefined)
  const [selectedMemberType, setSelectedMemberType] = useState<MemberType>(selectedMemberTabType)
  const [displayInternalUserCount, setDisplayInternalUserCount] = useState<number | null>(null)
  const [displayExternalUserCount, setDisplayExternalUserCount] = useState<number | null>(null)

  const { loading, users } = useGetUsers()
  const { externalUsers } = useGetExternalUsers()
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const { profile } = useGetProfile()

  const displayedUserCountTxt = useMemo(() => {
    if (displayInternalUserCount === null && displayExternalUserCount === null) {
      return
    }

    return selectedMemberType === 'internal' ? displayInternalUserCount : displayExternalUserCount
  }, [displayInternalUserCount, displayExternalUserCount, selectedMemberType])

  const totalUserCountTxt = useMemo(() => {
    if (!users || !externalUsers) {
      return
    }

    const targetUsers: UserEntity[] = selectedMemberType === 'internal' ? users : externalUsers
    return `${targetUsers.length} user`
  }, [users, externalUsers, selectedMemberType])

  return (
    <div className={className}>
      <div className="tw-ml-auto tw-w-fit">
        <div className="tw-flex tw-items-center tw-gap-s0">
          {users && externalUsers && (
            <div className="tw-flex tw-gap-r-3">
              <Txt sizeType="s" className="tw-font-bold tw-text-roovPrimary-default">
                {displayedUserCountTxt}
              </Txt>
              <Txt sizeType="s" className="tw-text-standardText-disable">
                /
              </Txt>
              <Txt sizeType="s" className="tw-text-standardText-secondary">
                {totalUserCountTxt}
              </Txt>
            </div>
          )}
          <Search
            onChange={setSearchWord}
            placeholder="お名前・メールアドレス・カスタムロール・キーワードで検索（複数可）"
            className="tw-w-[30rem]"
          />
        </div>
      </div>

      {!loading && profile && (
        <div className="-tw-mt-s3">
          <MemberTypeSwitchTabs
            selected={selectedMemberType}
            select={setSelectedMemberType}
            className="tw-w-fit"
          />
          {selectedMemberType === 'internal' ? (
            <InternalUserList
              users={users}
              profile={profile}
              searchWord={searchWord}
              updateDisplayInternalUserCount={setDisplayInternalUserCount}
            />
          ) : (
            <ExternalUserList
              externalUsers={externalUsers}
              searchWord={searchWord}
              updateDisplayInternalUserCount={setDisplayExternalUserCount}
            />
          )}
        </div>
      )}

      <CreateUserModal isOpen={showCreateUserModal} onClose={() => setShowCreateUserModal(false)} />
    </div>
  )
}
